import { Link } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { ALPHA } from "../../theme/tokens";
import { FROM_WAGE_BREAKDOWN_STATE_KEY, type WageBreakdownDestination, type WageBreakdownOrigin } from "../../utils/navigation/wageBreakdownReturn";
import { HoverCard } from "../UIToolkit/Popups/HoverCard";
import { useBreakdownPanel } from "./BreakdownPanelContext";

type Props = {
	to: string;
	/** The page `to` is, carried in the way back so its click reports where the link led. */
	destination: WageBreakdownDestination;
	label: ReactNode;
	/** Already translated: names the destination when the visible label alone does not. */
	ariaLabel?: string;
	onClick: () => void;
	dataTestId: string;
	/** Extra attributes a caller reports on (Wage Settings links carry their `data-section`). */
	dataSection?: string;
	/** `strong` for a link standing on its own; `subtle` for a name inside a breakdown line. */
	emphasis: "strong" | "subtle";
	/** The configuration rows behind the link (a `RuleCard`), shown on hover and focus. Omitted: no card. */
	hoverCard?: ReactNode;
	/**
	 * The destination's own Back goes where the breakdown was (the nurse profile reads `navigationSrc`), as
	 * well as offering "Back to wage breakdown".
	 */
	backToOrigin?: boolean;
};

/**
 * Every link out of the wage breakdown — to a Wage Settings rule, the nurse's profile, Agency
 * Configuration. A real `<a href>`, so it is keyboard-reachable and announces as a link. It stays in
 * this tab: the destination is another page of the app. One style, so the panel's links read as one family.
 *
 * Each carries where the breakdown was in history state (`fromWageBreakdown`), so the page it opens can
 * offer "Back to wage breakdown" — only to someone who arrived this way, never on a pasted URL or refresh.
 */
export const BreakdownRouteLink: FC<Props> = ({ to, destination, label, ariaLabel, onClick, dataTestId, dataSection, emphasis, hoverCard, backToOrigin }) => {
	const { shiftId, isActual, detailsOpen } = useBreakdownPanel();
	const location = useLocation();
	const returnTo = `${location.pathname}${location.search}`;
	const origin: WageBreakdownOrigin = { returnTo, shiftId, isActual, detailsOpen, destination };
	const link = (
		<Link
			component={RouterLink}
			to={to}
			state={{ [FROM_WAGE_BREAKDOWN_STATE_KEY]: origin, ...(backToOrigin ? { navigationSrc: returnTo } : {}) }}
			onClick={onClick}
			underline="hover"
			aria-label={ariaLabel}
			data-testid={dataTestId}
			data-section={dataSection}
			sx={(theme) => ({
				font: "inherit",
				fontWeight: emphasis === "strong" ? 600 : 500,
				color: theme.palette.primary.main,
				borderRadius: 0.5,
				"&:focus-visible": { outline: "none", boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
			})}
		>
			{label}
		</Link>
	);
	return hoverCard === undefined ? link : <HoverCard content={hoverCard}>{link}</HoverCard>;
};
